import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  FileText, Store, TrendingUp,
  BookMarked,
} from "lucide-react";
import MainLayout from "./layout/MainLayout";
import SidebarLayout from "./layout/SidebarLayout";
import FullWidthLayout from "./layout/FullWidthLayout";
import AppShell from "./layout/AppShell";
import ProtectedRoute from "./components/ProtectedRoute";
import SignalsRouteTracker from "./components/SignalsRouteTracker";
import OnboardingRequiredRoute from "./components/OnboardingRequiredRoute";

const Home = lazy(() => import("./pages/Home"));
const PoisePage = lazy(() => import("./pages/PoisePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const FeedPostRoute = lazy(() => import("./components/FeedPostRoute"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const OnboardingWizardPage = lazy(() => import("./pages/OnboardingWizardPageNew"));
const WhyVerifyPage = lazy(() => import("./pages/WhyVerifyPage"));
const EmploymentVerificationSubmitPage = lazy(() => import("./pages/EmploymentVerificationSubmitPage"));
const EmploymentVerificationConfirmPage = lazy(() => import("./pages/EmploymentVerificationConfirmPage"));
const PublicDocsPage = lazy(() => import("./pages/PublicDocsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SignupSuccessPage = lazy(() => import("./pages/SignupSuccessPage"));
const ActivateAccountPage = lazy(() => import("./pages/ActivateAccountPage"));
const ReactivateAccountPage = lazy(() => import("./pages/ReactivateAccountPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SurveysPage = lazy(() => import("./pages/SurveysPage"));
const DatabaseHomePage = lazy(() => import("./pages/database/DatabaseHomePage"));
const DatabaseFolderPage = lazy(() => import("./pages/database/DatabaseFolderPage"));
const DatabaseDocumentPage = lazy(() => import("./pages/database/DatabaseDocumentPage"));
const MyContributionsPage = lazy(() => import("./pages/database/MyContributionsPage"));
const MySurveysPage = lazy(() => import("./pages/MySurveysPage"));
const SurveyBuilderPage = lazy(() => import("./pages/SurveyBuilderPage"));
const SurveyDetailPage = lazy(() => import("./pages/SurveyDetailPage"));
const SurveyResultsPage = lazy(() => import("./pages/SurveyResultsPage"));
const SurveyRespondPage = lazy(() => import("./pages/SurveyRespondPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventBuilderPage = lazy(() => import("./pages/EventBuilderPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const CompanyDirectoryPage = lazy(() => import("./pages/CompanyDirectoryPage"));
const CompanyProfilePage = lazy(() => import("./pages/CompanyProfilePage"));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="h-8 w-8 rounded-full border-[3px] border-gray-200 border-t-primary animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <SignalsRouteTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Poise — standalone event landing page (no site nav/footer chrome) */}
            <Route path="/poise" element={<PoisePage />} />

            <Route path="/" element={<MainLayout />}>
              {/* Public routes */}
              <Route index element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/signup-success" element={<SignupSuccessPage />} />
              <Route path="/activate-account" element={<ActivateAccountPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/docs" element={<PublicDocsPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reactivate-account" element={<ReactivateAccountPage />} />

              {/* Shareable post link — auth-aware: logged-in users get the
                  full sidebar + interactions, logged-out visitors get a
                  read-only view with a signup CTA. */}
              <Route path="/feed/:postId" element={<FeedPostRoute />} />

              {/* Notifications - requires login only; wrapped in AppShell
                  so mobile users get the bottom nav and desktop users the
                  sidebar, without forcing onboarding. */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <NotificationsPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Onboarding - requires login */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizardPage />
                  </ProtectedRoute>
                }
              />

              {/* App pages — require login + onboarding, share the sidebar layout */}
              <Route
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <SidebarLayout />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              >
                <Route path="/feed"      element={<FeedPage />} />
                <Route path="/documents" element={<ComingSoonPage icon={FileText}       title="Documents"  description="A central hub for all your HR documents, templates, and policies — organised, searchable, and shareable with your team." detail="We're building a powerful document management experience tailored for HR professionals." />} />
                <Route path="/database" element={<DatabaseHomePage />} />
                <Route path="/database/folders/:folderId" element={<DatabaseFolderPage />} />
                <Route path="/database/documents/:documentId" element={<DatabaseDocumentPage />} />
                <Route path="/database/contributions" element={<MyContributionsPage />} />
                <Route path="/vendors"   element={<ComingSoonPage icon={Store}          title="Vendors"    description="Browse and compare HR software vendors, service providers, and consultants reviewed by your peers." detail="Community-driven, unbiased vendor reviews from HR professionals like you." />} />
                <Route path="/insights"  element={<ComingSoonPage icon={TrendingUp}     title="Insights"   description="Data-driven trends, benchmarks, and industry insights curated specifically for HR professionals." detail="Stay ahead with real-time data on hiring, compensation, retention, and more." />} />
                <Route path="/surveys"             element={<SurveysPage />} />
                <Route path="/surveys/mine"       element={<MySurveysPage />} />
                <Route path="/surveys/create"     element={<SurveyBuilderPage />} />
                <Route path="/surveys/:id"        element={<SurveyDetailPage />} />
                <Route path="/surveys/:id/results" element={<SurveyResultsPage />} />
                <Route path="/surveys/:id/edit"   element={<SurveyBuilderPage />} />
                <Route path="/surveys/:id/respond" element={<SurveyRespondPage />} />
                <Route path="/events"             element={<EventsPage />} />
                <Route path="/events/create"      element={<EventBuilderPage />} />
                <Route path="/events/:id"         element={<EventDetailPage />} />
                <Route path="/events/:id/edit"    element={<EventBuilderPage />} />
                <Route path="/resources" element={<ComingSoonPage icon={BookMarked}     title="Resources"  description="A curated library of HR frameworks, legal guides, research papers, and best-practice playbooks." detail="Everything you need to stay informed and effective in your role." />} />
                <Route path="/settings"  element={<Navigate to="/profile?tab=settings" replace />} />
              </Route>

              {/* Companies — require login + onboarding, but render full-width
                  WITHOUT the left navigation (like /docs). They carry their own
                  in-page navigation (directory search, profile tabs). */}
              <Route
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <FullWidthLayout />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              >
                <Route path="/companies"       element={<CompanyDirectoryPage />} />
                <Route path="/companies/:slug" element={<CompanyProfilePage />} />
              </Route>

              {/* Why Verify - public page */}
              <Route path="/why-verify" element={<WhyVerifyPage />} />

              {/* Employment verification - requires login + onboarding */}
              <Route
                path="/employment-verification"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <EmploymentVerificationSubmitPage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employment-verify"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <EmploymentVerificationConfirmPage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />

              {/* Profile - requires login + onboarding */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <ProfilePage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
